'use client'

import { useKeyboardShortcut } from '@/hooks/useKeyboardShortcut/useKeyboardShortcut'
import { Autocomplete, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import { KeyboardEvent, useCallback, useMemo, useRef } from 'react'
import { FiSearch, FiX } from 'react-icons/fi'

export interface SearchInputProps {
    value: string
    onChange: (value: string) => void
    onSubmit: (value: string) => void
    onClear: () => void
    placeholder?: string
    fullWidth?: boolean
    disabled?: boolean
}

const ShortcutHint = ({ isApple }: { isApple: boolean }) => (
    <Tooltip arrow title={isApple ? 'Use command + K to search' : 'Use ctrl + K to search'}>
        <span className="inline-flex items-center gap-1 select-none cursor-default" aria-hidden>
            <kbd
                className={clsx(
                    'inline-flex items-center justify-center',
                    'h-5.5 min-w-5.5 rounded-md',
                    'font-medium text-foreground-muted bg-black/5 dark:bg-white/10',
                    isApple ? 'text-base' : 'text-[10px] px-1.5'
                )}
            >
                {isApple ? '⌘' : 'Ctrl'}
            </kbd>
            <kbd className="inline-flex h-5.5 min-w-5.5 items-center justify-center rounded-md bg-black/5 text-[11px] font-medium text-foreground-muted dark:bg-white/10">
                K
            </kbd>
        </span>
    </Tooltip>
)

/** 1 = hint → clear (forward), -1 = clear → hint (reverse) */
const endAdornmentVariants = {
    initial: (direction: number) => ({
        opacity: 0,
        x: 6 * direction,
        filter: 'blur(2px)',
    }),
    animate: {
        opacity: 1,
        x: 0,
        filter: 'blur(0px)',
    },
    exit: (direction: number) => ({
        opacity: 0,
        x: -6 * direction,
        filter: 'blur(2px)',
    }),
}

const endAdornmentTransition = { duration: 0.16, ease: 'easeOut' as const }

export const SearchInput = ({ value, onChange, onSubmit, onClear, placeholder = 'Search', fullWidth, disabled }: SearchInputProps) => {
    const inputRef = useRef<HTMLInputElement | null>(null)
    const hasValue = Boolean(value)
    const adornmentDirection = hasValue ? 1 : -1

    const isApple = useMemo(() => {
        if (typeof navigator === 'undefined') return true
        return /Mac|iPhone|iPad|iPod/i.test(navigator.platform) || navigator.userAgent.includes('Mac')
    }, [])

    const shortcutOptions = useMemo(() => ({ ...(isApple ? { metaKey: true } : { ctrlKey: true }), enabled: !disabled }), [isApple, disabled])

    const focusSearch = useCallback(() => {
        const input = inputRef.current
        if (!input || disabled) return
        // Defer so we run after the keydown finishes (avoids Autocomplete swallowing focus).
        requestAnimationFrame(() => {
            input.focus()
            input.select()
        })
    }, [disabled])

    useKeyboardShortcut('K', focusSearch, shortcutOptions)

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            const nextValue = event.currentTarget?.value?.trim() || (event.target as HTMLInputElement)?.value?.trim() || ''
            onSubmit(nextValue)
        }
    }

    return (
        <div className="flex items-center gap-1 shrink-0">
            <div className={clsx('w-full', !fullWidth && 'min-w-xs max-w-xs')}>
                <Autocomplete
                    freeSolo
                    fullWidth
                    options={[]}
                    disableClearable
                    size="small"
                    disabled={disabled}
                    value={value}
                    renderInput={(params) => {
                        const { ref: paramsRef, ...inputProps } = params.inputProps

                        return (
                            <TextField
                                {...params}
                                placeholder={placeholder}
                                value={value}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: 'var(--border-color)',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'var(--border-color-focus)',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'var(--border-color-focus)',
                                        },
                                    },
                                }}
                                onChange={(event) => {
                                    const nextValue = event.target.value
                                    onChange(nextValue)
                                    if (nextValue === '') {
                                        onClear()
                                    }
                                }}
                                onKeyDown={handleKeyDown}
                                slotProps={{
                                    htmlInput: {
                                        ...inputProps,
                                        type: 'text',
                                        'data-testid': 'search-input',
                                        ref: (node: HTMLInputElement | null) => {
                                            inputRef.current = node
                                            if (typeof paramsRef === 'function') {
                                                paramsRef(node)
                                            } else if (paramsRef) {
                                                ;(paramsRef as React.MutableRefObject<HTMLInputElement | null>).current = node
                                            }
                                        },
                                    },
                                    input: {
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <FiSearch className="ml-2! text-base!" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: disabled ? undefined : (
                                            <InputAdornment position="end" className="mr-0.5! relative min-w-12 justify-end!">
                                                <AnimatePresence mode="wait" initial={false} custom={adornmentDirection}>
                                                    {hasValue ? (
                                                        <motion.div
                                                            key="clear"
                                                            className="flex items-center justify-center"
                                                            custom={adornmentDirection}
                                                            variants={endAdornmentVariants}
                                                            initial="initial"
                                                            animate="animate"
                                                            exit="exit"
                                                            transition={endAdornmentTransition}
                                                        >
                                                            <IconButton
                                                                aria-label="Clear search"
                                                                onClick={onClear}
                                                                size="small"
                                                                className="border-none! font-medium!"
                                                            >
                                                                <FiX className="text-sm! text-inherit!" />
                                                            </IconButton>
                                                        </motion.div>
                                                    ) : (
                                                        <motion.div
                                                            key="shortcut"
                                                            className="flex items-center -mb-px"
                                                            custom={adornmentDirection}
                                                            variants={endAdornmentVariants}
                                                            initial="initial"
                                                            animate="animate"
                                                            exit="exit"
                                                            transition={endAdornmentTransition}
                                                        >
                                                            <ShortcutHint isApple={isApple} />
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            height: '40px',
                                        },
                                    },
                                }}
                            />
                        )
                    }}
                />
            </div>
        </div>
    )
}
